<template>
  <a-modal
    :visible="visible"
    :width="780"
    :closable="false"
    :maskClosable="false"
    :footer="null"
    @cancel="handleClose"
    wrapClassName="register-modal-wrap"
  >
    <template slot="title">
      <div class="custom-modal-header">
        <span class="header-title">登记缴费信息</span>
        <span class="header-close" @click="handleClose">&times;</span>
      </div>
    </template>
    <div class="modal-container">
      <!-- 内容区域 -->
      <div class="modal-body">
        <a-spin :spinning="loading">
          <!-- 顶部通知提示条 -->
          <div v-if="showNoticeBanner" class="notice-banner">
            <span class="notice-icon">✓</span>
            <span class="notice-text">您如已缴费到站点，请填写以下信息</span>
          </div>

          <!-- 不显示站点缴费区域 -->
          <div v-if="!showSection && !loading" class="not-open">
            <p>当前教学点暂未开放站点缴费登记</p>
          </div>

          <!-- 未提交 / 重新提交状态 -->
          <div v-else-if="showSection && !sitePaymentInfo">
            <div class="form-section">
              <div class="form-item">
                <label class="form-label">缴费说明</label>
                <a-textarea
                  v-model="paymentDesc"
                  placeholder="请填写线下缴费相关说明、缴费时间、经手老师等信息"
                  :maxLength="500"
                  :autoSize="{ minRows: 3, maxRows: 5 }"
                />
              </div>
              <div class="form-item">
                <label class="form-label">
                  缴费截图 <span class="required">*</span>
                </label>
                <a-upload
                  listType="picture"
                  :action="uploadAction"
                  :headers="uploadHeaders"
                  :beforeUpload="beforeUpload"
                  :fileList="fileList"
                  @change="handleUploadChange"
                  accept=".jpg,.jpeg,.png"
                >
                  <a-button class="upload-btn">
                    <a-icon type="upload" /> 点击上传
                  </a-button>
                </a-upload>
                <p class="upload-tip">支持 jpg/png/jpeg 格式，大小不超过 5MB</p>
              </div>
            </div>
          </div>

          <!-- 已提交审核中 approveStatus=0 -->
          <div v-else-if="sitePaymentInfo && sitePaymentInfo.approveStatus === 0" class="status-info">
            <div class="status-tag tag-pending">审核中</div>
            <div class="info-detail">
              <p v-if="sitePaymentInfo.paymentDesc"><strong>缴费说明：</strong>{{ sitePaymentInfo.paymentDesc }}</p>
              <p v-if="sitePaymentInfo.paymentScreenshot">
                <strong>缴费截图：</strong>
                <img :src="sitePaymentInfo.paymentScreenshot" class="screenshot-preview" />
              </p>
            </div>
          </div>

          <!-- 审核通过 approveStatus=1 -->
          <div v-else-if="sitePaymentInfo && sitePaymentInfo.approveStatus === 1" class="status-info">
            <div class="status-tag tag-approved">已通过</div>
            <div class="info-detail">
              <p v-if="sitePaymentInfo.paymentDesc"><strong>缴费说明：</strong>{{ sitePaymentInfo.paymentDesc }}</p>
              <p v-if="sitePaymentInfo.paymentScreenshot">
                <strong>缴费截图：</strong>
                <img :src="sitePaymentInfo.paymentScreenshot" class="screenshot-preview" />
              </p>
            </div>
          </div>

          <!-- 已拒绝 approveStatus=2 -->
          <div v-else-if="sitePaymentInfo && sitePaymentInfo.approveStatus === 2" class="status-info">
            <div class="status-tag tag-rejected">已拒绝</div>
            <div class="info-detail">
              <p v-if="sitePaymentInfo.paymentDesc"><strong>缴费说明：</strong>{{ sitePaymentInfo.paymentDesc }}</p>
              <p v-if="sitePaymentInfo.paymentScreenshot">
                <strong>缴费截图：</strong>
                <img :src="sitePaymentInfo.paymentScreenshot" class="screenshot-preview" />
              </p>
              <p v-if="sitePaymentInfo.approveMessage" class="reject-reason">
                <strong>拒绝原因：</strong>{{ sitePaymentInfo.approveMessage }}
              </p>
            </div>
          </div>

          <!-- 错误信息 -->
          <div v-if="errorMsg" class="error-info">
            <a-alert type="error" :message="errorMsg" show-icon />
          </div>
        </a-spin>
      </div>
      <!-- 自定义 Footer -->
      <div class="modal-footer" v-if="showSection && !sitePaymentInfo">
        <button class="btn-primary" :disabled="submitting" @click="handleSubmit">
          {{ submitting ? '提交中...' : '提交' }}
        </button>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: 'RegisterModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      showSection: false,
      sitePaymentInfo: null,
      paymentDesc: '',
      fileList: [],
      uploadedUrl: '',
      uploadAction: '',
      uploadHeaders: {},
      errorMsg: '',
    };
  },
  computed: {
    showNoticeBanner() {
      // 未提交状态才展示通知条，已拒绝状态不展示
      if (this.showSection && !this.sitePaymentInfo) return true;
      return false;
    },
  },
  created() {
    this.uploadAction = `${this.axios.defaults.baseURL}/common/upload`;
  },
  watch: {
    visible(val) {
      if (val) {
        this.uploadHeaders = { 'X-Access-Token': localStorage.getItem('token') };
        this.fetchCheckInfo();
      }
    },
  },
  methods: {
    fetchCheckInfo() {
      this.loading = true;
      this.errorMsg = '';
      this.showSection = false;
      this.sitePaymentInfo = null;
      this.paymentDesc = '';
      this.fileList = [];
      this.uploadedUrl = '';

      this.axios
        .get('/student/sitePayment/check')
        .then(res => {
          this.loading = false;
          this.showSection = res.result.showSection;
          this.sitePaymentInfo = res.result.sitePaymentInfo || null;
        })
        .catch(() => {
          this.loading = false;
          this.errorMsg = '获取登记信息失败，请稍后再试';
        });
    },
    beforeUpload(file) {
      const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 <= 5;
      if (!isImage) {
        this.$message.error('只能上传 jpg/png/jpeg 格式的图片');
        return false;
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB');
        return false;
      }
      return true;
    },
    handleUploadChange(info) {
      if (info.file.status === 'done') {
        if (info.file.response && info.file.response.code === 200) {
          this.uploadedUrl = info.file.response.result || '';
          this.fileList = [info.file];
          this.$message.success('上传成功');
        } else {
          this.$message.error(
            info.file.response
              ? info.file.response.message || '上传失败'
              : '上传失败'
          );
          this.fileList = [];
          this.uploadedUrl = '';
        }
      } else if (info.file.status === 'uploading') {
        this.fileList = [info.file];
      } else if (info.file.status === 'error') {
        this.$message.error('上传失败，请重试');
        this.fileList = [];
        this.uploadedUrl = '';
      } else if (info.file.status === 'removed') {
        this.fileList = [];
        this.uploadedUrl = '';
      }
    },
    handleSubmit() {
      if (!this.uploadedUrl) {
        this.$message.warning('请上传缴费截图');
        return;
      }
      this.submitting = true;
      this.axios
        .post('/student/sitePayment/submit', {
          paymentDesc: this.paymentDesc,
          paymentScreenshot: this.uploadedUrl,
        })
        .then(res => {
          this.submitting = false;
          this.$message.success('提交成功');
          this.handleClose();
        })
        .catch(() => {
          this.submitting = false;
          this.$message.error('提交失败，请稍后再试');
        });
    },
    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="stylus" scoped>
.modal-container
  background #fff

.custom-modal-header
  background #fff
  padding 16px 20px
  display flex
  justify-content space-between
  align-items center
  .header-title
    color #333
    font-size 16px
    font-weight 600
  .header-close
    color #333
    font-size 22px
    cursor pointer
    line-height 1
    opacity 0.85
    &:hover
      opacity 1

.modal-body
  padding 20px 24px
  min-height 200px

.notice-banner
  display flex
  align-items center
  background #F5F7FA
  border-radius 8px
  padding 12px 16px
  margin 0 0 16px 0

.notice-icon
  display flex
  align-items center
  justify-content center
  width 24px
  height 24px
  border-radius 50%
  background #1890ff
  color #fff
  font-size 14px
  margin-right 12px
  flex-shrink 0

.notice-text
  color #333
  font-size 14px
  line-height 1.5

.not-open
  text-align center
  padding 40px 0
  color #999

.form-section
  padding 4px 0

.form-item
  margin-bottom 20px

.form-label
  display block
  font-size 14px
  color #333
  margin-bottom 8px
  font-weight 500

.required
  color #f5222d

.upload-btn
  background #fff
  border 1px solid #D9D9D9
  color #666
  border-radius 6px
  &:hover
    color #1890ff
    border-color #1890ff

.upload-tip
  font-size 12px
  color #999
  margin-top 4px

.status-info
  padding 10px 0

.status-tag
  display inline-block
  padding 4px 12px
  border-radius 12px
  font-size 13px
  font-weight 500
  margin-bottom 16px

.tag-pending
  background #fff7e6
  color #fa8c16
  border 1px solid #ffd591

.tag-approved
  background #f6ffed
  color #52c41a
  border 1px solid #b7eb8f

.tag-rejected
  background #fff1f0
  color #f5222d
  border 1px solid #ffa39e

.info-detail
  p
    margin 8px 0
    font-size 14px
    color #333

.screenshot-preview
  display block
  max-width 100%
  max-height 300px
  margin-top 8px
  border 1px solid #e8e8e8
  border-radius 8px

.reject-reason
  color #f5222d
  background #fff1f0
  padding 10px
  border-radius 8px
  margin-top 12px !important

.error-info
  margin-top 16px

.modal-footer
  display flex
  justify-content center
  align-items center
  gap 10px
  padding 16px 24px 20px

.btn-primary
  background #1890ff
  color #fff
  border none
  border-radius 22px
  padding 0 24px
  min-width 100px
  height 36px
  cursor pointer
  font-size 14px
  outline none
  &:hover
    background #096dd9
  &[disabled]
    opacity 0.6
    cursor not-allowed

.btn-secondary
  background #fff
  color #666
  border 1px solid #e8e8e8
  border-radius 22px
  padding 0 24px
  min-width 100px
  height 36px
  cursor pointer
  font-size 14px
  outline none
  &:hover
    background #E6F7FF
    color #1890ff
</style>

<style lang="stylus">
.register-modal-wrap
  .ant-modal-content
    border-radius 14px
    overflow hidden
  .ant-modal-header
    padding 0
    background transparent
    border-bottom 1px solid #e8e8e8
  .ant-modal-body
    padding 0
    background #fff
  .ant-modal-footer
    display none
  .ant-modal-close
    display none
</style>



// WEBPACK FOOTER //
// src/pages/home/components/RegisterModal.vue